import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

export default function AuthDebug() {
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [envInfo, setEnvInfo] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      try {
        // Get environment info
        setEnvInfo({
          'Current URL': window.location.href,
          'Origin': window.location.origin,
          'Supabase URL': import.meta.env.VITE_SUPABASE_URL || 'Not set',
          'Has Supabase Key': import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Yes' : 'No',
          'Node Environment': import.meta.env.MODE,
        });

        // Check current session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          setSessionInfo({ error: error.message });
        } else {
          setSessionInfo(data);
        }
      } catch (e) {
        console.error('Auth check error:', e);
        setSessionInfo({ error: String(e) });
      } finally {
        setLoading(false);
      }
    }

    checkAuth();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const handleGetSession = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      alert(`Session error: ${error.message}`);
    } else {
      setSessionInfo(data);
      alert('Session refreshed');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      console.log('Sign in initiated:', data);
    } catch (e) {
      console.error('Sign in error:', e);
      alert(`Sign in error: ${e}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-gray-100">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Auth Debug Page</h1>

        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-semibold mb-4">Environment Information</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <tbody>
                {Object.entries(envInfo).map(([key, value]) => (
                  <tr key={key} className="border-b">
                    <td className="py-2 px-4 font-medium">{key}</td>
                    <td className="py-2 px-4">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-semibold mb-4">Session Information</h2>
          <div className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
            <pre className="text-sm">{JSON.stringify(sessionInfo, null, 2)}</pre>
          </div>
        </div>

        <div className="flex space-x-4 mb-8">
          <button
            onClick={handleGetSession}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Session
          </button>

          <button
            onClick={handleGoogleSignIn}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Sign In with Google
          </button>

          {sessionInfo?.session && (
            <button
              onClick={handleSignOut}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Sign Out
            </button>
          )}
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}